import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Layout, Row, Col, Card, Typography, message, Form, Modal } from "antd";
import dayjs from "dayjs";
import { selectAppPermissions } from "../../../store/auth/selectors";
import { getEntregasPaginadas, getEntregasStats, getEntregasFiltros, deleteEntrega, updateEntrega } from "../../../services/financiero/finEntregasService";
import { getParticipantes, getProductos, getMunicipios, getDepartamentos, getProyectos } from "../../../services/financiero/finOtrosService";
import EntregasHeader from "./EntregasHeader";
import EntregasCards from "./components/EntregasCards";
import EntregasFilters from "./components/EntregasFilters";
import EntregasTable from "./components/EntregasTable";
import EntregasModal from "./components/EntregasModal";
import EntregaForm from "./components/EntregaForm";
const { Content } = Layout;
const { Text } = Typography;

export default function EntregasPage() {

  const appName = "entregas_financiero";
  const permissions = useSelector((state) => selectAppPermissions(state, appName));
  const user = useSelector((state) => state.auth.user);
  const userLogin = user?.username || "system";
  const permissionAccess = permissions?.priv_access === "Y";
  const permissionDelete = permissions?.priv_delete === "Y";
  const permissionUpdate = permissions?.priv_update === "Y";
  const permissionInsert = permissions?.priv_insert === "Y";
  const permissionExport = permissions?.priv_export === "Y";

  const [loading, setLoading] = useState(false);
  const [entregas, setEntregas] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [idsConEntregas, setIdsConEntregas] = useState({ proyectos: [], municipios: [] });
  const [participantes, setParticipantes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [editingEntrega, setEditingEntrega] = useState(null);
  const [form] = Form.useForm();

  const [filters, setFilters] = useState({
    search: "",
    archivo: "",
    proyecto: [],
    departamento: null,
    municipio: null,
    producto: null,
    tipo_entrega: null
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  // Solo se aplica la respuesta de la consulta más reciente (al escribir en
  // los filtros pueden quedar varias en curso).
  const consultaRef = useRef(0);

  const cargarEntregas = async (page = pagination.current, pageSize = pagination.pageSize, filtrosActuales = filters) => {
    const consulta = ++consultaRef.current;
    try {
      setLoading(true);
      const [res, statsRes] = await Promise.all([
        getEntregasPaginadas({ page, limit: pageSize, filters: filtrosActuales }),
        getEntregasStats(filtrosActuales)
      ]);
      if (consulta !== consultaRef.current) return;
      // Si se eliminó lo último de la página, se vuelve a la última que exista.
      const ultimaPagina = Math.max(1, Math.ceil(res.total / res.limit));
      if (res.page > ultimaPagina) {
        cargarEntregas(ultimaPagina, pageSize, filtrosActuales);
        return;
      }
      setEntregas(res.data || []);
      setStats(statsRes);
      setPagination({ current: res.page, pageSize: res.limit, total: res.total });
    } catch (error) {
      if (consulta !== consultaRef.current) return;
      console.error(error);
      message.error("Error cargando entregas");
    } finally {
      if (consulta === consultaRef.current) setLoading(false);
    }
  };

  const cargarFiltros = async () => {
    try {
      setIdsConEntregas(await getEntregasFiltros());
    } catch (error) {
      console.error(error);
    }
  };

  const recargar = () => {
    cargarEntregas();
    cargarFiltros();
  };

  const cargarCatalogos = async () => {
    try {
      const [
        participantesData,
        productosData,
        municipiosData,
        departamentosData,
        proyectosData
      ] = await Promise.all([
        getParticipantes(),
        getProductos(),
        getMunicipios(),
        getDepartamentos(),
        getProyectos()
      ]);
      setParticipantes(participantesData || []);
      setProductos(productosData || []);
      setMunicipios(municipiosData || []);
      setDepartamentos(departamentosData || []);
      setProyectos(proyectosData || []);
    } catch (error) {

      console.error(error);
      message.error("Error cargando catálogos");
    }
  };

  useEffect(() => {
    if (permissionAccess) {
      cargarFiltros();
      cargarCatalogos();
    }
  }, [permissionAccess]);

  // Los filtros se aplican en el servidor; se espera a que el usuario deje de
  // escribir para no lanzar una consulta por cada tecla.
  useEffect(() => {
    if (!permissionAccess) return;
    const timer = setTimeout(() => {
      cargarEntregas(1, pagination.pageSize, filters);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionAccess, filters]);

  const filtrosCatalogos = useMemo(() => {
    const proyectosIds = new Set(idsConEntregas.proyectos);
    const municipiosIds = new Set(idsConEntregas.municipios);
    const municipiosConEntregas = municipios.filter(m => municipiosIds.has(m.oid));
    const deptosIds = new Set(municipiosConEntregas.map(m => m.oid_depto));

    return {
      proyectos: proyectos.filter(p => proyectosIds.has(p.oid)),
      municipios: municipiosConEntregas,
      departamentos: departamentos.filter(d => deptosIds.has(d.oid))
    };
  }, [idsConEntregas, proyectos, municipios, departamentos]);

  const handleDelete = async (record) => {
    try {
      await deleteEntrega(record.oid, userLogin);
      message.success("Entrega eliminada");
      cargarEntregas();
    } catch (error) {
      console.error(error);
      message.error("Error eliminando");
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedRows.length) {
      message.warning("Seleccione registros");
      return;
    }
    try {
      await Promise.all(
        selectedRows.map((row) =>
          deleteEntrega(row.oid, userLogin)
        )
      );
      message.success("Registros eliminados");
      setSelectedRows([]);
      cargarEntregas();
    } catch (error) {
      console.error(error);
      message.error("Error eliminando registros");
    }
  };

  const handleEdit = (record) => {
    setEditingEntrega(record);
  };


  const handleUpdate = async (values) => {
    try {
      // El backend actualiza todas las columnas de fin_entregas, así que los
      // datos del acta que este formulario no edita se reenvían tal cual para
      // que no queden en NULL.
      const payload = {
        lugar_entrega: editingEntrega.lugar_entrega,
        tipo_acta: editingEntrega.tipo_acta,
        responsable: editingEntrega.responsable,
        oid_usuario_entrega: editingEntrega.oid_usuario_entrega,
        oid_usuario_supervisor: editingEntrega.oid_usuario_supervisor,
        representante_legal_nombre: editingEntrega.representante_legal_nombre,
        representante_legal_cedula: editingEntrega.representante_legal_cedula,
        camara_comercio_codigo: editingEntrega.camara_comercio_codigo,
        camara_comercio_fecha: editingEntrega.camara_comercio_fecha
          ? dayjs(editingEntrega.camara_comercio_fecha).format("YYYY-MM-DD")
          : null,
        telefono_contacto_entrega: editingEntrega.telefono_contacto_entrega,
        ...values,
        carpeta: values.carpeta,
        nombre_archivo: values.nombre_archivo,
        fecha_entrega: values.fecha_entrega
          ? values.fecha_entrega.format("YYYY-MM-DD")
          : null
      };
      await updateEntrega(editingEntrega.oid, payload, userLogin);
      message.success("Entrega actualizada");
      setEditingEntrega(null);
      form.resetFields();
      cargarEntregas();
    } catch (error) {
      console.error(error);
      message.error("Error actualizando entrega");
    }
  };

  if (!permissionAccess) {
    return (
      <Content style={{ padding: 24 }}>
        <Card>
          <Text>No tienes permisos para acceder a este módulo.</Text>
        </Card>
      </Content>
    );
  }

  return (
    <div className="container">
      <Content style={{ padding: 24 }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <EntregasHeader
              onCreate={() => setModalOpen(true)}
              onRefresh={recargar}
              onDeleteSelected={handleDeleteSelected}
              permissionInsert={permissionInsert}
              permissionDelete={permissionDelete}
              loading={loading}
              selectedRows={selectedRows}
            />
          </Col>
          <Col span={24}>
            <EntregasCards stats={stats} />
          </Col>
          <Col span={24}>
            <EntregasFilters
              filters={filters}
              setFilters={setFilters}
              departamentos={filtrosCatalogos.departamentos}
              municipios={filtrosCatalogos.municipios}
              proyectos={filtrosCatalogos.proyectos}
              userLogin={userLogin}
            />
          </Col>
          <Col span={24}>
            <EntregasTable
              data={entregas}
              loading={loading}
              pagination={pagination}
              onPageChange={(page, pageSize) => cargarEntregas(page, pageSize)}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              onDelete={handleDelete}
              onEdit={handleEdit}
              permissionDelete={permissionDelete}
              permissionUpdate={permissionUpdate}
            />
          </Col>
          {editingEntrega && (
            <Col span={24}>
              <Modal
                title="Editar entrega"
                open={!!editingEntrega}
                onCancel={() => {
                  setEditingEntrega(null);
                  form.resetFields();
                }}
                onOk={() => form.submit()}
                okText="Actualizar"
                cancelText="Cancelar"
                width={1000}
              >
                <EntregaForm
                  form={form}
                  initialValues={editingEntrega}
                  participantes={participantes}
                  productos={productos}
                  municipios={municipios}
                  departamentos={departamentos}
                  proyectos={proyectos}
                  onFinish={handleUpdate}
                />
              </Modal>
            </Col>
          )}
        </Row>
        <EntregasModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          participantes={participantes}
          productos={productos}
          municipios={municipios}
          departamentos={departamentos}
          proyectos={proyectos}
          userLogin={userLogin}
          reload={recargar}
        />
      </Content>
    </div>
  );
}